//! Wrapper de fetch com interceptor global de 401.
//!
//! Problema: JWT expira (15min), ou admin remoto revoga, ou outra aba faz
//! logout. Sem isso, requests viram 401 mudos e o usuario continua na tela
//! "logado" (localStorage ainda tem token) ate o proximo refresh manual.
//!
//! Solucao:
//! - `authedFetch(scope, token, url, opts)` adiciona Bearer + intercepta 401
//! - Em 401, despacha CustomEvent("saquei:auth-expired", { detail: { scope } })
//! - Em main.tsx, listener captura, chama clearAuth(scope), redireciona e toast
//! - Antes do request, checa exp do JWT localmente; se ja expirou, dispara
//!   o mesmo evento sem nem tentar fetch
//! - `storage` event listener detecta logout em outra aba (key removida) e
//!   despacha o evento tambem

import { ApiError } from "../api";

export type AuthScope = "tenant" | "super";

const AUTH_EXPIRED_EVENT = "saquei:auth-expired";

interface AuthExpiredDetail {
    scope: AuthScope;
    reason: "401" | "local_exp" | "cross_tab";
}

export function onAuthExpired(handler: (detail: AuthExpiredDetail) => void): () => void {
    function listener(e: Event) {
        const ce = e as CustomEvent<AuthExpiredDetail>;
        handler(ce.detail);
    }
    window.addEventListener(AUTH_EXPIRED_EVENT, listener);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, listener);
}

function emitAuthExpired(scope: AuthScope, reason: AuthExpiredDetail["reason"]) {
    window.dispatchEvent(
        new CustomEvent<AuthExpiredDetail>(AUTH_EXPIRED_EVENT, {
            detail: { scope, reason },
        }),
    );
}

/** Decodifica o payload do JWT (sem verificar assinatura) e retorna `exp` em segundos UNIX. */
export function jwtExpSeconds(token: string): number | null {
    try {
        const parts = token.split(".");
        if (parts.length !== 3) return null;
        const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
        return typeof payload.exp === "number" ? payload.exp : null;
    } catch {
        return null;
    }
}

/** Token ainda valido (com margem de 5s pra evitar request que ja vai chegar expirado)? */
export function isJwtValid(token: string): boolean {
    const exp = jwtExpSeconds(token);
    if (exp === null) return true; // sem exp, deixa servidor decidir
    return exp > Math.floor(Date.now() / 1000) + 5;
}

interface FetchOptions extends Omit<RequestInit, "headers"> {
    headers?: Record<string, string>;
}

/**
 * Fetch autenticado com interceptor global de 401 e checagem local de expiracao.
 *
 * - Se token expirou localmente: emite evento sem chamar servidor; lanca ApiError(401).
 * - Faz request com Authorization: Bearer.
 * - Se response.status === 401: emite evento + lanca ApiError(401).
 * - Outros erros propagam pelo throw normal do ApiError.
 */
export async function authedFetch(
    scope: AuthScope,
    token: string,
    url: string,
    opts: FetchOptions = {},
): Promise<Response> {
    // Cookie HttpOnly e a fonte primaria do JWT agora (`credentials: include`).
    // Token legacy (vazio em sessoes novas) ainda vai como Authorization
    // header pra compat. Se ambos ausentes, backend usa cookie -- so falha
    // se cookie tambem invalido.
    const headers: Record<string, string> = { ...(opts.headers ?? {}) };
    if (token) {
        // Checa exp local: economiza round-trip se ja sabemos expirado.
        if (!isJwtValid(token)) {
            emitAuthExpired(scope, "local_exp");
            throw new ApiError(401, "Sessão expirada");
        }
        headers.authorization = `Bearer ${token}`;
    }
    const r = await fetch(url, { ...opts, headers, credentials: "include" });
    if (r.status === 401) {
        emitAuthExpired(scope, "401");
        let msg = "Sessão expirada";
        try {
            const b = await r.clone().json();
            if (b?.error?.message) msg = String(b.error.message);
        } catch {}
        throw new ApiError(401, msg);
    }
    return r;
}

/**
 * Helper conveniente: faz fetch + parse JSON. Trata 2xx -> T, !ok -> ApiError.
 */
export async function authedJson<T>(
    scope: AuthScope,
    token: string,
    url: string,
    opts: FetchOptions = {},
): Promise<T> {
    const r = await authedFetch(scope, token, url, opts);
    if (!r.ok) {
        let msg = `HTTP ${r.status}`;
        try {
            const b = await r.json();
            if (b?.error?.message) msg = String(b.error.message);
        } catch {}
        throw new ApiError(r.status, msg);
    }
    return (await r.json()) as T;
}

/**
 * Listener global de `storage` event: outra aba removeu a chave de auth.
 * Esta aba detecta e propaga internamente como auth-expired (cross_tab).
 *
 * Chamar uma vez no main.tsx.
 */
export function installCrossTabAuthSync(opts: {
    tenantStorageKey: string;
    superStorageKey: string;
}) {
    window.addEventListener("storage", (e) => {
        if (e.storageArea !== window.localStorage) return;
        if (e.newValue !== null) return; // so reagimos a remocao
        if (e.key === opts.tenantStorageKey) {
            emitAuthExpired("tenant", "cross_tab");
        } else if (e.key === opts.superStorageKey) {
            emitAuthExpired("super", "cross_tab");
        }
    });
}

/**
 * Monkey-patch global do `window.fetch` que intercepta 401 em qualquer
 * request com header `Authorization: Bearer ...`. Cobre TODOS os call sites
 * existentes sem precisar refatorar caso-a-caso. Scope eh inferido pela
 * URL (`/api/v1/super/*` -> super; resto -> tenant).
 *
 * Chamar uma vez no main.tsx ANTES de qualquer fetch.
 */
export function installFetchAuthInterceptor() {
    const original = window.fetch.bind(window);
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
        const r = await original(input, init);
        if (r.status !== 401) return r;
        // So intercepta se request tinha header Authorization Bearer (era
        // autenticada -- nao queremos disparar logout em /signup ou /login
        // que ja retornam 401 como parte do flow normal).
        const hadAuth = hasBearerHeader(input, init);
        if (!hadAuth) return r;
        const url = typeof input === "string"
            ? input
            : input instanceof URL
                ? input.toString()
                : input.url;
        const scope: AuthScope = url.includes("/api/v1/super/") ? "super" : "tenant";
        emitAuthExpired(scope, "401");
        return r;
    };
}

function hasBearerHeader(input: RequestInfo | URL, init?: RequestInit): boolean {
    if (init?.headers) {
        if (init.headers instanceof Headers) {
            const v = init.headers.get("authorization");
            if (v && v.toLowerCase().startsWith("bearer ")) return true;
        } else if (Array.isArray(init.headers)) {
            for (const [k, v] of init.headers) {
                if (k.toLowerCase() === "authorization" && v.toLowerCase().startsWith("bearer ")) {
                    return true;
                }
            }
        } else {
            for (const [k, v] of Object.entries(init.headers)) {
                if (k.toLowerCase() === "authorization"
                    && typeof v === "string"
                    && v.toLowerCase().startsWith("bearer ")) {
                    return true;
                }
            }
        }
    }
    if (input instanceof Request) {
        const v = input.headers.get("authorization");
        if (v && v.toLowerCase().startsWith("bearer ")) return true;
    }
    return false;
}
